import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ScheduleEditor from './components/ScheduleEditor';
import ProjectBrowser from './components/ProjectBrowser';
import Toolbar from './components/Toolbar';
import PrintView from './components/PrintView';
import VersionHistory from './components/VersionHistory';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { Eye, Pencil, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Debounce function
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const DEFAULT_COLUMNS = [
  { id: 'time', label: 'Time', width: 15 },
  { id: 'scene', label: 'Scene', width: 15 },
  { id: 'location', label: 'Location', width: 23 },
  { id: 'cast', label: 'Cast', width: 23 },
  { id: 'notes', label: 'Notes', width: 24 },
];

const genId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : String(Date.now()) + Math.random().toString(16).slice(2);

// Kurz-Slug = erstes Wort des Namens (muss zur Backend-Logik _slug passen)
const slugify = (name) => {
  const first = (name || '').trim().split(/\s+/)[0] || '';
  return first.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

// Frisches Untitled-Projekt
function makeNewProject() {
  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
  return {
    name: 'Untitled Project',
    notes: '',
    logo_url: '',
    column_widths: { time: 15, scene: 15, location: 23, cast: 23, notes: 24 },
    column_headers: { time: 'Time', scene: 'Scene', location: 'Location', cast: 'Cast', notes: 'Notes' },
    columns: DEFAULT_COLUMNS.map((c) => ({ ...c })),
    days: [
      {
        id: genId(),
        date: formattedDate,
        position: 0,
        icon: 'calendar',
        rows: [{ id: genId(), type: 'item', time: '', scene: '', location: '', cast: '', notes: '' }],
      },
    ],
    calltimes: [],
    crewInfos: [],
  };
}

// Sorgt dafür, dass ein Projekt eine columns-Liste hat (Migration für Alt-Projekte).
function migrateProject(p) {
  if (!p) return p;
  let columns = Array.isArray(p.columns) && p.columns.length ? p.columns : null;
  if (!columns) {
    const ch = p.column_headers || {};
    const cw = p.column_widths || {};
    columns = DEFAULT_COLUMNS.map((c) => ({
      id: c.id,
      label: ch[c.id] || c.label,
      width: cw[c.id] || c.width,
    }));
  }
  columns = columns.map((c, i) => ({
    id: c.id || `col_${i}_${Math.random().toString(16).slice(2)}`,
    label: c.label ?? '',
    width: c.width ?? 15,
  }));
  // Calltimes ebenfalls auf columns migrieren
  const calltimes = (p.calltimes || []).map((ct) => {
    let cc = Array.isArray(ct.columns) && ct.columns.length ? ct.columns : null;
    if (!cc) {
      const h = ct.headers || {};
      cc = [
        { id: 'time', label: h.time || 'Time', width: 18 },
        { id: 'name', label: h.name || 'Name', width: 82 },
      ];
    }
    cc = cc.map((c, i) => ({
      id: c.id || `cc_${i}_${Math.random().toString(16).slice(2)}`,
      label: c.label ?? '',
      width: c.width ?? 20,
    }));
    return { ...ct, columns: cc };
  });
  // CrewInfos auf columns migrieren
  const crewInfos = (p.crewInfos || []).map((ci) => {
    let cc = Array.isArray(ci.columns) && ci.columns.length ? ci.columns : null;
    if (!cc) {
      const h = ci.headers || {};
      cc = [
        { id: 'name', label: h.name || 'Name', width: 25 },
        { id: 'crew', label: h.crew || 'Crew', width: 20 },
        { id: 'info', label: h.info || 'Info', width: 55 },
      ];
    }
    cc = cc.map((c, i) => ({
      id: c.id || `ci_${i}_${Math.random().toString(16).slice(2)}`,
      label: c.label ?? '',
      width: c.width ?? 20,
    }));
    return { ...ci, columns: cc };
  });
  return { ...p, columns, calltimes, crewInfos };
}

function App({ openSlug }) {
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState({ active: [], archived: [] });
  const [showBrowser, setShowBrowser] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [conflict, setConflict] = useState(null); // {server_rev, current} bei Fremdänderung
  const initialLoad = useRef(true);
  const printTitleRef = useRef(null);
  const dirtyRef = useRef(false); // ungespeicherte lokale Änderungen?
  const currentRef = useRef(null);
  currentRef.current = currentProject;

  // Debounce project changes for autosave
  const debouncedProject = useDebounce(currentProject, 1500);

  // Initialize: entweder ein per Kurz-URL angefordertes Projekt öffnen, sonst neu anlegen
  useEffect(() => {
    loadProjects();
    if (openSlug) {
      openBySlug(openSlug);
    } else {
      setCurrentProject(makeNewProject());
    }
  }, []);

  // Autosave effect
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    if (debouncedProject && debouncedProject.name && debouncedProject.name !== 'Untitled Project') {
      handleSave();
    }
  }, [debouncedProject]);

  // Live-Abgleich: prüft alle 7s, ob ein anderer Tab/PC das Projekt geändert hat.
  // Ohne lokale ungespeicherte Änderungen wird still neu geladen, sonst Konflikt-Banner.
  useEffect(() => {
    const id = setInterval(async () => {
      const cur = currentRef.current;
      if (!cur?.id || saving || conflict) return;
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/projects/${cur.id}/meta`);
        const localRev = cur.rev || 0;
        if (data.rev > localRev) {
          if (!dirtyRef.current) {
            const fresh = await axios.get(`${BACKEND_URL}/api/projects/${cur.id}`);
            initialLoad.current = true;
            setCurrentProject(migrateProject(fresh.data));
            toast('Von anderem Gerät aktualisiert', { icon: '🔄' });
          } else {
            const fresh = await axios.get(`${BACKEND_URL}/api/projects/${cur.id}`);
            setConflict({ server_rev: data.rev, current: fresh.data });
          }
        }
      } catch (e) {
        /* offline/Netzfehler ignorieren */
      }
    }, 7000);
    return () => clearInterval(id);
  }, [saving, conflict]);

  // --- Teilen: Kurz-Links in die Zwischenablage ------------------------------
  const copyToClipboard = async (text, okMsg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg);
    } catch (e) {
      // Fallback: Prompt zeigt den Link zum manuellen Kopieren
      window.prompt('Link kopieren:', text);
    }
  };

  const projectSlug = () => slugify(currentProject?.name || '');

  const handleCopyPdfLink = () => {
    const slug = projectSlug();
    if (!slug) {
      toast.error('Bitte erst einen Projektnamen vergeben');
      return;
    }
    if (dirtyRef.current || !currentProject?.id) {
      toast('Wird gespeichert – Link gilt nach dem Speichern', { icon: '💾' });
    }
    copyToClipboard(`${window.location.origin}/${slug}.pdf`, 'PDF-Link kopiert');
  };

  const handleCopyProjectLink = () => {
    const slug = projectSlug();
    if (!slug) {
      toast.error('Bitte erst einen Projektnamen vergeben');
      return;
    }
    copyToClipboard(`${window.location.origin}/${slug}`, 'Projekt-Link kopiert');
  };

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects?include_archived=true`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const handleProjectChange = (updatedProject) => {
    dirtyRef.current = true;
    setCurrentProject(updatedProject);
  };

  const handleSave = async () => {
    if (!currentProject) return;

    setSaving(true);
    try {
      let response;

      // If project has ID, update it. Otherwise create new
      if (currentProject.id) {
        response = await axios.put(`${BACKEND_URL}/api/projects/${currentProject.id}`, currentProject);
      } else {
        response = await axios.post(`${BACKEND_URL}/api/projects/save`, currentProject);
      }

      initialLoad.current = true; // Server-Antwort soll keinen erneuten Autosave triggern
      setCurrentProject(response.data);
      dirtyRef.current = false;
      setConflict(null);
      await loadProjects();
    } catch (error) {
      // 409 = jemand anderes (anderer Tab/PC) hat zwischenzeitlich gespeichert
      if (error?.response?.status === 409) {
        const d = error.response.data?.detail || {};
        dirtyRef.current = true;
        setConflict({ server_rev: d.server_rev, current: d.current });
      } else {
        console.error('Save failed:', error);
        toast.error('Speichern fehlgeschlagen');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLoadProject = async (projectId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${projectId}`);
      initialLoad.current = true; // Prevent autosave on load
      setCurrentProject(migrateProject(response.data));
      dirtyRef.current = false;
      setConflict(null);
      setShowBrowser(false);
      toast.success('Projekt geladen');
    } catch (error) {
      console.error('Load failed:', error);
      toast.error('Projekt konnte nicht geladen werden');
    }
  };

  // Projekt über die Kurz-URL (erstes Wort) öffnen
  const openBySlug = async (slug) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/by/${encodeURIComponent(slug)}`);
      initialLoad.current = true;
      setCurrentProject(migrateProject(response.data));
      dirtyRef.current = false;
      toast.success(`Projekt geladen: ${response.data.name}`);
    } catch (error) {
      toast.error(`Kein Projekt für „${slug}" gefunden`);
      setCurrentProject(makeNewProject());
    }
  };

  // Aktuelle Server-Version übernehmen (eigene ungespeicherte Änderungen verwerfen)
  const reloadFromServer = () => {
    if (conflict?.current) {
      initialLoad.current = true;
      setCurrentProject(migrateProject(conflict.current));
      dirtyRef.current = false;
      setConflict(null);
      toast.success('Aktuelle Version geladen');
    } else if (currentRef.current?.id) {
      handleLoadProject(currentRef.current.id);
      setConflict(null);
    }
  };

  // Eigene Version trotzdem speichern (überschreibt die fremde Änderung bewusst)
  const overwriteWithMine = async () => {
    if (!currentProject || !conflict) return;
    const forced = { ...currentProject, rev: conflict.server_rev };
    setConflict(null);
    initialLoad.current = true;
    setCurrentProject(forced);
    // direkt speichern (rev == server_rev -> Backend akzeptiert)
    setSaving(true);
    try {
      const response = await axios.put(`${BACKEND_URL}/api/projects/${forced.id}`, forced);
      initialLoad.current = true;
      setCurrentProject(response.data);
      dirtyRef.current = false;
      toast.success('Deine Version gespeichert');
      await loadProjects();
    } catch (e) {
      toast.error('Überschreiben fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`);
      toast.success('Project deleted');
      await loadProjects();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete project');
    }
  };

  const handleExportCSV = async () => {
    if (!currentProject || !currentProject.id) {
      toast.error('Please wait for project to save first');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${currentProject.id}/export.csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${currentProject.name}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    }
  };

  // --- JSON Export / Import ---------------------------------------------------
  // Erzeugt ein sauberes, KI-freundliches JSON (ohne interne IDs/Zeitstempel).
  const handleExportJSON = () => {
    if (!currentProject) return;
    const clean = {
      name: currentProject.name,
      notes: currentProject.notes || '',
      logo_url: currentProject.logo_url || '',
      columns: (currentProject.columns || DEFAULT_COLUMNS).map((c) => ({ label: c.label, width: c.width })),
      days: (currentProject.days || []).map((d) => ({
        date: d.date,
        icon: d.icon || 'calendar',
        rows: (d.rows || []).map((r) =>
          r.type === 'text'
            ? { type: 'text', text: r.text != null ? r.text : (r.notes || '') }
            : {
                type: 'item',
                cells: Object.fromEntries(
                  (currentProject.columns || DEFAULT_COLUMNS).map((c) => [c.label, r[c.id] != null ? r[c.id] : ''])
                ),
              }
        ),
      })),
      calltimes: (currentProject.calltimes || []).map((c) => {
        const cc = (c.columns && c.columns.length)
          ? c.columns
          : [
              { id: 'time', label: c.headers?.time || 'Time', width: 18 },
              { id: 'name', label: c.headers?.name || 'Name', width: 82 },
            ];
        return {
          title: c.title || 'Calltime',
          icon: c.icon || 'clock',
          columns: cc.map((col) => ({ label: col.label, width: col.width })),
          rows: (c.rows || []).map((r) =>
            r.type === 'text'
              ? { type: 'text', text: r.text != null ? r.text : (r.name || '') }
              : { type: 'item', cells: Object.fromEntries(cc.map((col) => [col.label, r[col.id] != null ? r[col.id] : ''])) }
          ),
        };
      }),
      crewInfos: (currentProject.crewInfos || []).map((c) => {
        const cc = (c.columns && c.columns.length)
          ? c.columns
          : [
              { id: 'name', label: c.headers?.name || 'Name', width: 25 },
              { id: 'crew', label: c.headers?.crew || 'Crew', width: 20 },
              { id: 'info', label: c.headers?.info || 'Info', width: 55 },
            ];
        return {
          title: c.title || 'Crew Info',
          icon: c.icon || 'phone',
          columns: cc.map((col) => ({ label: col.label, width: col.width })),
          rows: (c.rows || []).map((r) =>
            r.type === 'text'
              ? { type: 'text', text: r.text != null ? r.text : (r.name || '') }
              : { type: 'item', cells: Object.fromEntries(cc.map((col) => [col.label, r[col.id] != null ? r[col.id] : ''])) }
          ),
        };
      }),
      textBlocks: (currentProject.textBlocks || []).map((tb) => ({
        text: tb.text || '',
        width: tb.width || '100%',
        height: tb.height || '120px',
        color: tb.color || null,
      })),
    };

    const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    // Dateiname = Projektname (nur dateisystem-kritische Zeichen ersetzen)
    const safeName = (currentProject.name || 'drehplan').replace(/[\/\\:*?"<>|]+/g, '-').trim() || 'drehplan';
    link.setAttribute('download', `${safeName}.json`);
    document.body.appendChild(link);
    link.click();
    // Aufräumen verzögern – sofortiges revokeObjectURL bricht den Download in Chrome ab
    setTimeout(() => {
      link.remove();
      window.URL.revokeObjectURL(url);
    }, 1000);
    toast.success('JSON exportiert');
  };

  // Normalisiert importiertes JSON: vergibt frische IDs + Positionen, füllt Defaults.
  const normalizeImported = (data) => {
    const uid = genId;
    // Spalten: neues Format (data.columns) bevorzugt, sonst Standard-5
    let columns;
    if (Array.isArray(data.columns) && data.columns.length) {
      const n = data.columns.length;
      columns = data.columns.map((c, i) => ({
        id: uid(),
        label: c.label || `Spalte ${i + 1}`,
        width: c.width || Math.round(100 / n),
      }));
    } else {
      columns = DEFAULT_COLUMNS.map((c) => ({ ...c }));
    }
    const labelToId = {};
    columns.forEach((c) => { labelToId[c.label] = c.id; });
    const LEGACY = ['time', 'scene', 'location', 'cast', 'notes'];

    const mapRow = (r) => {
      const base = { id: uid(), type: r.type === 'text' ? 'text' : 'item' };
      if (base.type === 'text') {
        base.text = r.text != null ? r.text : (r.notes || '');
        return base;
      }
      if (r.cells && typeof r.cells === 'object') {
        // Neues Format: cells nach Spalten-Label
        Object.entries(r.cells).forEach(([label, val]) => {
          const id = labelToId[label];
          if (id) base[id] = val != null ? String(val) : '';
        });
      } else {
        // Legacy: flache Felder time/scene/location/cast/notes
        LEGACY.forEach((k, idx) => {
          if (r[k] === undefined) return;
          const direct = columns.find((c) => c.id === k);
          if (direct) base[k] = r[k] || '';
          else if (columns[idx]) base[columns[idx].id] = r[k] || '';
        });
      }
      return base;
    };

    const days = (data.days || []).map((d, i) => ({
      id: uid(),
      date: d.date || '',
      icon: d.icon || 'calendar',
      position: i,
      rows: (d.rows || []).map(mapRow),
    }));
    const calltimes = (data.calltimes || []).map((c, i) => {
      let cc;
      if (Array.isArray(c.columns) && c.columns.length) {
        const n = c.columns.length;
        cc = c.columns.map((col, j) => ({
          id: uid(),
          label: col.label || `Spalte ${j + 1}`,
          width: col.width || Math.round(100 / n),
        }));
      } else {
        cc = [
          { id: 'time', label: c.headers?.time || 'Time', width: 18 },
          { id: 'name', label: c.headers?.name || 'Name', width: 82 },
        ];
      }
      const l2id = {};
      cc.forEach((col) => { l2id[col.label] = col.id; });
      const mapCtRow = (r) => {
        const base = { id: uid(), type: r.type === 'text' ? 'text' : 'item' };
        if (base.type === 'text') {
          base.text = r.text != null ? r.text : (r.name || '');
          return base;
        }
        if (r.cells && typeof r.cells === 'object') {
          Object.entries(r.cells).forEach(([label, val]) => {
            const id = l2id[label];
            if (id) base[id] = val != null ? String(val) : '';
          });
        } else {
          ['time', 'name'].forEach((k, idx) => {
            if (r[k] === undefined) return;
            const direct = cc.find((col) => col.id === k);
            if (direct) base[k] = r[k] || '';
            else if (cc[idx]) base[cc[idx].id] = r[k] || '';
          });
        }
        return base;
      };
      return {
        id: uid(),
        title: c.title || 'Calltime',
        icon: c.icon || 'clock',
        columns: cc,
        position: days.length + i,
        rows: (c.rows || []).map(mapCtRow),
      };
    });
    const crewInfos = (data.crewInfos || []).map((c, i) => {
      let cc;
      if (Array.isArray(c.columns) && c.columns.length) {
        const n = c.columns.length;
        cc = c.columns.map((col, j) => ({ id: uid(), label: col.label || `Spalte ${j + 1}`, width: col.width || Math.round(100 / n) }));
      } else {
        cc = [
          { id: 'name', label: c.headers?.name || 'Name', width: 25 },
          { id: 'crew', label: c.headers?.crew || 'Crew', width: 20 },
          { id: 'info', label: c.headers?.info || 'Info', width: 55 },
        ];
      }
      const l2id = {};
      cc.forEach((col) => { l2id[col.label] = col.id; });
      const mapCiRow = (r) => {
        const base = { id: uid(), type: r.type === 'text' ? 'text' : 'item' };
        if (base.type === 'text') {
          base.text = r.text != null ? r.text : (r.name || '');
          return base;
        }
        if (r.cells && typeof r.cells === 'object') {
          Object.entries(r.cells).forEach(([label, val]) => {
            const id = l2id[label];
            if (id) base[id] = val != null ? String(val) : '';
          });
        } else {
          ['name', 'crew', 'info'].forEach((k, idx) => {
            if (r[k] === undefined) return;
            const direct = cc.find((col) => col.id === k);
            if (direct) base[k] = r[k] || '';
            else if (cc[idx]) base[cc[idx].id] = r[k] || '';
          });
        }
        return base;
      };
      return {
        id: uid(),
        title: c.title || 'Crew Info',
        icon: c.icon || 'phone',
        columns: cc,
        position: days.length + calltimes.length + i,
        rows: (c.rows || []).map(mapCiRow),
      };
    });
    const textBlocks = (data.textBlocks || []).map((tb, i) => ({
      id: uid(),
      text: tb.text || '',
      width: tb.width || '100%',
      height: tb.height || '120px',
      color: tb.color || null,
      position: days.length + calltimes.length + crewInfos.length + i,
    }));
    return {
      name: data.name || 'Importierter Drehplan',
      notes: data.notes || '',
      logo_url: data.logo_url || '',
      columns,
      days,
      calltimes,
      crewInfos,
      textBlocks,
    };
  };

  const handleImportJSON = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        const normalized = normalizeImported(data);
        if (!normalized.days.length && !normalized.calltimes.length && !normalized.crewInfos.length) {
          toast.error('JSON enthält keine Tage/Calltimes/Crew-Infos');
          return;
        }
        // Ohne id -> wird als neues Projekt gespeichert (Upsert nach Name).
        setCurrentProject(normalized);
        toast.success('JSON importiert – wird automatisch gespeichert');
      } catch (err) {
        console.error('Import failed:', err);
        toast.error('Ungültige JSON-Datei');
      }
    };
    reader.readAsText(file);
  };

  const handlePrint = () => {
    setShowPrintView(true);
    // PDF-Dateiname = Projektname (Browser nutzt document.title als Default)
    printTitleRef.current = document.title;
    document.title = currentProject?.name || 'Drehplan';
    setTimeout(() => {
      window.print();
    }, 100);
  };

  useEffect(() => {
    const handleAfterPrint = () => {
      setShowPrintView(false);
      if (printTitleRef.current !== null) {
        document.title = printTitleRef.current;
        printTitleRef.current = null;
      }
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, []);

  if (!currentProject) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (showPrintView) {
    return <PrintView project={currentProject} />;
  }

  return (
    <div className={`App ${previewMode ? 'preview-mode' : ''}`}>
      <button
        onClick={() => setPreviewMode((v) => !v)}
        className="screen-only fixed top-3 right-3 z-[60] flex items-center gap-2 px-3 py-2 rounded-md shadow-md border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50"
        title={previewMode ? 'Bearbeiten' : 'Vorschau'}
        data-testid="preview-toggle"
      >
        {previewMode ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        {previewMode ? 'Bearbeiten' : 'Vorschau'}
      </button>

      {previewMode && (
        <div className="max-w-[210mm] mx-auto px-8 pt-14 pb-2 preview-header">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 mb-1">{currentProject.name}</h1>
              {currentProject.notes && (
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{currentProject.notes}</p>
              )}
            </div>
            {currentProject.logo_url && (
              <img
                src={`${BACKEND_URL}${currentProject.logo_url}`}
                alt="Logo"
                className="max-w-[150px] max-h-[80px] object-contain ml-4"
              />
            )}
          </div>
        </div>
      )}

      {conflict && (
        <div className="no-print sticky top-0 z-[55] bg-amber-50 border-b border-amber-300 text-amber-900 px-4 py-2 flex flex-wrap items-center gap-3 text-sm">
          <span className="flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" />
            Dieser Drehplan wurde an anderer Stelle (anderer Tab/PC) geändert.
          </span>
          <span className="text-amber-700">Was möchtest du tun?</span>
          <button
            onClick={reloadFromServer}
            className="px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700"
          >
            Aktuelle Version laden
          </button>
          <button
            onClick={overwriteWithMine}
            className="px-2.5 py-1 rounded border border-amber-400 hover:bg-amber-100"
          >
            Meine Version behalten
          </button>
        </div>
      )}

      <Toolbar
        project={currentProject}
        onProjectChange={handleProjectChange}
        onBrowse={() => setShowBrowser(true)}
        onExportCSV={handleExportCSV}
        onExportJSON={handleExportJSON}
        onImportJSON={handleImportJSON}
        onPrint={handlePrint}
        onCopyPdfLink={handleCopyPdfLink}
        onCopyProjectLink={handleCopyProjectLink}
        onShowVersions={() => {
          if (!currentProject?.id) {
            toast('Projekt wird erst gespeichert…', { icon: '💾' });
            return;
          }
          setShowVersions(true);
        }}
        saving={saving}
      />
      
      <div className="container mx-auto px-4 py-6">
        <ScheduleEditor
          project={currentProject}
          onProjectChange={handleProjectChange}
        />
      </div>

      {showBrowser && (
        <ProjectBrowser
          projects={projects}
          onClose={() => setShowBrowser(false)}
          onLoad={handleLoadProject}
          onDelete={handleDeleteProject}
        />
      )}

      {showVersions && currentProject?.id && (
        <VersionHistory
          projectId={currentProject.id}
          onClose={() => setShowVersions(false)}
          onRestored={(restored) => {
            initialLoad.current = true;
            setCurrentProject(migrateProject(restored));
            dirtyRef.current = false;
            setConflict(null);
          }}
        />
      )}

      <Toaster position="bottom-right" />
    </div>
  );
}

// Reine Druckseite (/?print=<projekt-id>) – wird vom Backend-PDF-Renderer
// (Headless-Chromium) geladen, funktioniert aber auch direkt im Browser.
function PrintPage({ projectId }) {
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/projects/${encodeURIComponent(projectId)}`)
      .then((r) => {
        const p = migrateProject(r.data);
        document.title = p.name || 'Drehplan';
        setProject(p);
      })
      .catch((e) => setError(e?.response?.data?.detail || String(e)));
  }, [projectId]);

  if (error) {
    return <div data-print-error="1" style={{ padding: 24 }}>Fehler: {error}</div>;
  }
  if (!project) {
    return <div style={{ padding: 24 }}>Lade Drehplan…</div>;
  }
  return (
    <div data-print-ready="1">
      <PrintView project={project} />
    </div>
  );
}

// Pfade, die NICHT als Projekt-Slug interpretiert werden (echte Dateien/Routen)
const RESERVED_PATHS = new Set([
  'index.html', 'favicon.ico', 'manifest.json', 'asset-manifest.json',
  'robots.txt', 'logo192.png', 'logo512.png', 'static', 'api',
]);

function AppRouter() {
  const printId = new URLSearchParams(window.location.search).get('print');
  if (printId) {
    return <PrintPage projectId={printId} />;
  }
  // Kurz-URL: drehplan.offensivst.de/<erstes-wort> öffnet das passende Projekt
  const seg = decodeURIComponent((window.location.pathname || '/').split('/')[1] || '');
  const openSlug = seg && !RESERVED_PATHS.has(seg) && !seg.includes('.') ? seg : null;
  return <App openSlug={openSlug} />;
}

export default AppRouter;
